import { useCallback } from "react";
import { useConfigure, usePagination } from "react-instantsearch";

import { cn } from "@tudigo-monorepo/core-tudigo-theme";
import { useResponsiveGridLayout } from "@tudigo-monorepo/core-tudigo-utils";
import {
  PaginationRebrand,
  PaginationRebrandContent,
  PaginationRebrandEllipsis,
  PaginationRebrandFirst,
  PaginationRebrandItem,
  PaginationRebrandLast,
  PaginationRebrandLink,
  PaginationRebrandNext,
  PaginationRebrandPrevious,
} from "@tudigo-monorepo/web-tudigo-components";

type SearchProjectsPaginationProps = {
  onPageChange?: () => void;
};

export function SearchProjectsPagination({
  onPageChange,
}: SearchProjectsPaginationProps) {
  const { itemsPerPage } = useResponsiveGridLayout();
  const {
    pages,
    nbPages,
    isFirstPage,
    isLastPage,
    currentRefinement,
    canRefine: canChangePage,
    refine,
  } = usePagination();

  // Define Algolia number of hits per page based on current layout
  useConfigure({ hitsPerPage: itemsPerPage });

  const refinePage = useCallback(
    (page: number) => {
      if (page !== currentRefinement) {
        refine(page);
        onPageChange?.();
      }
    },
    [currentRefinement, refine, onPageChange],
  );

  const showFirst = useCallback(() => {
    refine(0);
    onPageChange?.();
  }, [refine, onPageChange]);

  const showPrevious = useCallback(() => {
    refine(currentRefinement - 1);
    onPageChange?.();
  }, [currentRefinement, refine, onPageChange]);

  const showLast = useCallback(() => {
    refine(nbPages - 1);
    onPageChange?.();
  }, [nbPages, refine, onPageChange]);

  const showMore = useCallback(() => {
    refine(currentRefinement + 1);
    onPageChange?.();
  }, [currentRefinement, refine, onPageChange]);

  if (!canChangePage) {
    return null;
  }

  return (
    <PaginationRebrand className="mt-6">
      <div className="flex items-center gap-x-4">
        <PaginationRebrandContent className="gap-x-1 sm:gap-x-2">
          <PaginationRebrandItem>
            <PaginationRebrandFirst
              onClick={showFirst}
              disabled={isFirstPage}
            />
          </PaginationRebrandItem>
          <PaginationRebrandItem>
            <PaginationRebrandPrevious
              onClick={showPrevious}
              disabled={isFirstPage}
            />
          </PaginationRebrandItem>
        </PaginationRebrandContent>

        <PaginationRebrandContent className="gap-x-1 sm:gap-x-2">
          <PaginationRebrandItem>
            <PaginationRebrandLink
              onClick={showFirst}
              isActive={currentRefinement === 0}
            >
              1
            </PaginationRebrandLink>
          </PaginationRebrandItem>

          <PaginationRebrandItem
            className={cn(
              "hidden",
              currentRefinement > 1 && "flex sm:hidden",
              currentRefinement > 2 && "sm:flex",
            )}
          >
            <PaginationRebrandEllipsis className="w-6 sm:w-auto" />
          </PaginationRebrandItem>

          {pages.map(
            (page) =>
              page !== 0 &&
              page !== nbPages - 1 && (
                <PaginationRebrandItem
                  key={page}
                  className={cn(
                    "hidden",
                    Math.abs(page - currentRefinement) < 1 && "flex sm:hidden",
                    Math.abs(page - currentRefinement) < 2 && "sm:flex",
                  )}
                >
                  <PaginationRebrandLink
                    onClick={() => refinePage(page)}
                    isActive={currentRefinement === page}
                  >
                    {page + 1}
                  </PaginationRebrandLink>
                </PaginationRebrandItem>
              ),
          )}

          <PaginationRebrandItem
            className={cn(
              "hidden",
              currentRefinement < nbPages - 2 && "flex sm:hidden",
              currentRefinement < nbPages - 3 && "sm:flex",
            )}
          >
            <PaginationRebrandEllipsis className="w-6 sm:w-auto" />
          </PaginationRebrandItem>

          {nbPages > 1 && (
            <PaginationRebrandItem>
              <PaginationRebrandLink
                onClick={showLast}
                isActive={currentRefinement === nbPages - 1}
              >
                {nbPages}
              </PaginationRebrandLink>
            </PaginationRebrandItem>
          )}
        </PaginationRebrandContent>

        <PaginationRebrandContent className="gap-x-1 sm:gap-x-2">
          <PaginationRebrandItem>
            <PaginationRebrandNext onClick={showMore} disabled={isLastPage} />
          </PaginationRebrandItem>
          <PaginationRebrandItem>
            <PaginationRebrandLast onClick={showLast} disabled={isLastPage} />
          </PaginationRebrandItem>
        </PaginationRebrandContent>
      </div>
    </PaginationRebrand>
  );
}
